import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { getApiUrl } from '@/lib/config';
import { useCreateChannel } from '../../hooks';
import { useInstagramEmbeddedSignup } from '../../hooks/useInstagramEmbeddedSignup';
import { Icon } from '@/shared/icons';
import { TokenStatusAlert, type TokenInfo } from './TokenStatusAlert';

const META_GRAPH_API_VERSION = 'v23.0';
const META_GRAPH_API_BASE = `https://graph.facebook.com/${META_GRAPH_API_VERSION}`;

const REQUIRED_INSTAGRAM_SCOPES = [
  'pages_show_list',
  'pages_read_engagement',
  'instagram_basic',
  'instagram_manage_messages',
];

interface MetaPageOption {
  id: string;
  name: string;
  pageAccessToken: string;
  instagramBusinessAccountId?: string;
}

async function fetchTokenInfo(token: string, requiredScopes: string[]): Promise<TokenInfo | null> {
  try {
    const url = `${META_GRAPH_API_BASE}/debug_token?input_token=${encodeURIComponent(token)}&access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.error || !data.data) return null;
    const scopes: string[] = data.data.scopes || [];
    const missingScopes = requiredScopes.filter((s) => !scopes.includes(s));
    return {
      expiresAt: typeof data.data.expires_at === 'number' ? data.data.expires_at : 0,
      scopes,
      isValid: data.data.is_valid !== false,
      missingScopes,
    };
  } catch {
    return null;
  }
}
/**
 * Form for connecting Instagram Direct
 *
 * Flow:
 * - Embedded: Facebook Login → Auto-configure → Show webhook URL
 * - Manual: Collect credentials → Test connection → Show webhook URL
 *
 * @example
 * <InstagramForm
 *   onComplete={(data) => handleAuthComplete(data)}
 * />
 */
export interface InstagramFormProps {
  /** Callback when authentication is complete */
  onComplete: (data?: InstagramData) => void;
}

export interface InstagramData {
  providerId: string;
  name: string;
  identifier: string;
  config?: {
    instagramAccountId?: string;
    pageId?: string;
    webhookToken?: string;
    webhookConfigured?: boolean;
  };
  credentials: {
    accessToken: string;
    instagramAccountId: string;
    pageId: string;
  };
}

type ConnectionStep = 'credentials' | 'connecting' | 'webhook' | 'complete';
type AuthMode = 'manual' | 'embedded';

export const InstagramForm: React.FC<InstagramFormProps> = ({
  onComplete,
}) => {
  const [step, setStep] = useState<ConnectionStep>('credentials');
  const [authMode, setAuthMode] = useState<AuthMode>('embedded');
  const [channelName, setChannelName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [instagramAccountId, setInstagramAccountId] = useState('');
  const [pageId, setPageId] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createChannel = useCreateChannel();

  // Instagram Embedded Signup hook
  const {
    startEmbeddedSignup,
    isLoading: isEmbeddedLoading,
    error: embeddedError,
    result: embeddedResult,
    reset: resetEmbedded,
    accountSelectionRequired,
    availableAccounts,
    selectInstagramAccount,
  } = useInstagramEmbeddedSignup();

  // Manual mode — Graph API discovery state
  const [isConnectingToken, setIsConnectingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [pages, setPages] = useState<MetaPageOption[]>([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [igDetectError, setIgDetectError] = useState<string | null>(null);
  const [isDetectingIg, setIsDetectingIg] = useState(false);
  const [igUsername, setIgUsername] = useState('');
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);

  // Handle Embedded Signup result - auto-create channel (only when NOT in account selection)
  useEffect(() => {
    if (embeddedResult && !accountSelectionRequired) {
      // Auto-fill credentials from embedded signup
      setAccessToken(embeddedResult.accessToken);
      setInstagramAccountId(embeddedResult.instagramAccountId);
      setPageId(embeddedResult.pageId);

      // Auto-create channel with embedded credentials
      handleEmbeddedChannelCreate(embeddedResult);
    }
  }, [embeddedResult, accountSelectionRequired]);

  const handleEmbeddedChannelCreate = async (result: typeof embeddedResult) => {
    if (!result) return;

    // Use default name or prompt user
    const autoChannelName = channelName.trim() || 'Instagram Direct';

    setStep('connecting');

    try {
      const channelRequest = {
        name: autoChannelName,
        identifier: result.username || result.instagramAccountId,
        providerId: 'instagram-messaging',
        config: {
          instagramAccountId: result.instagramAccountId,
          pageId: result.pageId,
          webhookToken: 'auto-generated',
          webhookConfigured: result.webhookConfigured,
        },
        credentials: {
          accessToken: result.accessToken,
          pageAccessToken: result.pageAccessToken,
          instagramAccountId: result.instagramAccountId,
          pageId: result.pageId,
        },
        integrationName: `${autoChannelName} Integration`,
        assignmentConfig: {
          enabled: true,
          type: 'sequential' as const,
        },
      };

      const channel = await createChannel.mutateAsync(channelRequest);

      // Webhook is auto-configured during channel creation (Step 6b in channels/service.ts)
      const integrationId = channel.integrationId;
      setWebhookUrl(`${getApiUrl()}/api/provider/webhook/${integrationId}`);
      setStep('webhook');
    } catch (error: any) {
      if (error.response?.status === 403) return; // Handled by global interceptor (toast)
      if (error.response?.status === 402) return; // Handled by global interceptor (toast)
      console.error('❌ Erro ao criar canal via Embedded Signup:', error);
      setErrorMessage(error.response?.data?.message || 'Erro ao criar canal. Tente novamente.');
      setStep('credentials');
      resetEmbedded();
    }
  };

  const resetManualDiscovery = () => {
    setPages([]);
    setSelectedPageId('');
    setPageId('');
    setInstagramAccountId('');
    setIgUsername('');
    setTokenError(null);
    setIgDetectError(null);
    setTokenInfo(null);
  };

  const handleConnectToken = async () => {
    if (!accessToken.trim()) {
      setTokenError('Cole seu Access Token primeiro');
      return;
    }

    setIsConnectingToken(true);
    setTokenError(null);
    resetManualDiscovery();

    try {
      const url = `${META_GRAPH_API_BASE}/me/accounts?fields=id,name,access_token,instagram_business_account&limit=100&access_token=${encodeURIComponent(accessToken.trim())}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.error) {
        setTokenError(data.error.message || 'Token inválido');
        return;
      }

      const pagesList: MetaPageOption[] = (data.data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        pageAccessToken: p.access_token,
        instagramBusinessAccountId: p.instagram_business_account?.id,
      }));

      if (pagesList.length === 0) {
        setTokenError('Nenhuma página do Facebook encontrada. Verifique se o token tem a permissão pages_show_list.');
        return;
      }

      setPages(pagesList);

      // Buscar metadata do token (expiração + scopes)
      const info = await fetchTokenInfo(accessToken.trim(), REQUIRED_INSTAGRAM_SCOPES);
      setTokenInfo(info);

      if (pagesList.length === 1) {
        await handleSelectPage(pagesList[0]!.id);
      }
    } catch (err) {
      setTokenError(err instanceof Error ? err.message : 'Erro ao conectar com o Meta');
    } finally {
      setIsConnectingToken(false);
    }
  };

  const handleSelectPage = async (newPageId: string) => {
    setSelectedPageId(newPageId);
    setPageId(newPageId);
    setInstagramAccountId('');
    setIgUsername('');
    setIgDetectError(null);

    if (!newPageId) return;

    const page = pages.find((p) => p.id === newPageId);
    if (!page) return;

    if (page.instagramBusinessAccountId) {
      setInstagramAccountId(page.instagramBusinessAccountId);
      await fetchInstagramDetails(page.instagramBusinessAccountId);
      return;
    }

    // Página sem IG vinculado na primeira chamada — tenta via endpoint da página
    setIsDetectingIg(true);
    try {
      const url = `${META_GRAPH_API_BASE}/${newPageId}?fields=instagram_business_account&access_token=${encodeURIComponent(accessToken.trim())}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.error) {
        setIgDetectError(data.error.message || 'Erro ao detectar Instagram');
        return;
      }

      const igId = data.instagram_business_account?.id;
      if (!igId) {
        setIgDetectError('Esta página não tem Instagram Business vinculado. Conecte uma conta Business/Creator no Instagram à página primeiro.');
        return;
      }

      setInstagramAccountId(igId);
      await fetchInstagramDetails(igId);
    } catch (err) {
      setIgDetectError(err instanceof Error ? err.message : 'Erro ao detectar Instagram');
    } finally {
      setIsDetectingIg(false);
    }
  };

  const fetchInstagramDetails = async (igId: string) => {
    try {
      const url = `${META_GRAPH_API_BASE}/${igId}?fields=id,username,name&access_token=${encodeURIComponent(accessToken.trim())}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!data.error && data.username) {
        setIgUsername(data.username);
        if (!channelName.trim()) {
          setChannelName(`Instagram @${data.username}`);
        }
      }
    } catch {
      // Detalhes são opcionais — silenciar
    }
  };

  const handleConnect = async () => {
    if (!accessToken.trim() || !instagramAccountId.trim() || !pageId.trim()) {
      setErrorMessage('Selecione uma página com Instagram Business vinculado');
      return;
    }

    const finalName = channelName.trim() || (igUsername ? `Instagram @${igUsername}` : 'Instagram Direct');
    if (finalName.length < 2) {
      setErrorMessage('Nome do canal deve ter pelo menos 2 caracteres');
      return;
    }

    setErrorMessage(null);
    setStep('connecting');

    try {
      const channelRequest = {
        name: finalName,
        identifier: instagramAccountId.trim(),
        providerId: 'instagram-messaging',
        config: {
          instagramAccountId: instagramAccountId.trim(),
          pageId: pageId.trim(),
          webhookToken: 'manual-setup',
          webhookConfigured: false,
        },
        credentials: {
          accessToken: accessToken.trim(),
          instagramAccountId: instagramAccountId.trim(),
          pageId: pageId.trim(),
        },
        integrationName: `${finalName} Integration`,
        assignmentConfig: {
          enabled: true,
          type: 'sequential' as const,
        },
      };

      const channel = await createChannel.mutateAsync(channelRequest);

      // Webhook is auto-configured during channel creation (Step 6b in channels/service.ts)
      const integrationId = channel.integrationId;
      setWebhookUrl(`${getApiUrl()}/api/provider/webhook/${integrationId}`);
      setStep('webhook');
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      if (axiosError.response?.status === 403) return; // Handled by global interceptor (toast)
      if (axiosError.response?.status === 402) return; // Handled by global interceptor (toast)
      const errorMsg = axiosError.response?.data?.message
        || (error instanceof Error ? error.message : 'Erro ao criar canal. Tente novamente.');
      console.error('❌ Erro ao criar canal manual:', error);
      setErrorMessage(errorMsg);
      setStep('credentials');
    }
  };

  const handleAccountSelect = async (account: typeof availableAccounts[0]) => {
    setErrorMessage(null);
    await selectInstagramAccount(account);
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
  };

  const handleComplete = () => {
    // Canal já criado em handleConnect / handleEmbeddedChannelCreate — apenas fecha
    onComplete();
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Credentials Input */}
      {step === 'credentials' && (
        <div className="space-y-4">
          {/* Auth Mode Selector */}
          <div
            className="flex rounded-lg overflow-hidden border"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            <button
              type="button"
              onClick={() => setAuthMode('embedded')}
              className="flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              style={{
                backgroundColor: authMode === 'embedded' ? 'var(--theme-button-primary)' : 'transparent',
                color: authMode === 'embedded' ? 'white' : 'var(--theme-card-text)',
              }}
            >
              <Icon name="facebook" size={18} />
              Login com Facebook
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('manual')}
              className="flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              style={{
                backgroundColor: authMode === 'manual' ? 'var(--theme-button-primary)' : 'transparent',
                color: authMode === 'manual' ? 'white' : 'var(--theme-card-text)',
              }}
            >
              <Icon name="key" size={18} />
              Credenciais Manuais
            </button>
          </div>

          {/* ============================================= */}
          {/* EMBEDDED MODE - Login com Facebook           */}
          {/* ============================================= */}
          {authMode === 'embedded' && (
            <div className="space-y-4">
              {/* Channel Name (optional for embedded) */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Nome do Canal (opcional)
                </label>
                <Input
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="Ex: Instagram Suporte"
                  className="w-full"
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Se não informado, usaremos "Instagram Direct"
                </p>
              </div>

              {/* Info Box about Facebook Login */}
              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: 'var(--theme-info-background)',
                  border: '1px solid var(--theme-info-color)',
                }}
              >
                <p
                  className="text-sm mb-3"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  <strong>Como funciona:</strong>
                </p>
                <ol
                  className="text-sm space-y-2 list-decimal list-inside"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  <li>Clique em "Conectar com Facebook"</li>
                  <li>Faça login com sua conta do Facebook</li>
                  <li>Selecione a página do Facebook vinculada ao Instagram</li>
                  <li>Autorize as permissões solicitadas</li>
                  <li>A configuração será feita automaticamente</li>
                </ol>
              </div>

              {/* Embedded Error */}
              {embeddedError && (
                <div
                  className="p-3 rounded-lg flex items-start gap-2"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                >
                  <Icon name="alert-circle" size={18}
                    style={{ color: 'var(--theme-error-color)' }}
                    className="flex-shrink-0 mt-0.5" />
                  <p
                    className="text-sm"
                    style={{ color: 'var(--theme-error-color)' }}
                  >
                    {embeddedError}
                  </p>
                </div>
              )}

              {/* General Error */}
              {errorMessage && (
                <div
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                >
                  <p
                    className="text-sm"
                    style={{ color: 'var(--theme-error-color)' }}
                  >
                    {errorMessage}
                  </p>
                </div>
              )}

              {/* Account Selection UI (shown after FB login when multiple accounts) */}
              {accountSelectionRequired && availableAccounts.length > 0 ? (
                <div className="space-y-3">
                  <div
                    className="p-3 rounded-lg flex items-start gap-2"
                    style={{
                      backgroundColor: 'var(--theme-success-background)',
                      border: '1px solid var(--theme-success-color)',
                    }}
                  >
                    <Icon name="check-circle" size={18}
                      style={{ color: 'var(--theme-success-color)' }}
                      className="flex-shrink-0 mt-0.5" />
                    <p
                      className="text-sm"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      Login realizado com sucesso! Selecione a conta Instagram que deseja conectar:
                    </p>
                  </div>

                  <div className="space-y-2">
                    {availableAccounts.map((account) => (
                      <button
                        key={account.id}
                        type="button"
                        onClick={() => handleAccountSelect(account)}
                        disabled={isEmbeddedLoading}
                        className="w-full p-3 rounded-lg border text-left transition-colors flex items-center gap-3"
                        style={{
                          borderColor: 'var(--theme-card-border)',
                          backgroundColor: 'var(--theme-card-background)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--theme-button-primary)';
                          e.currentTarget.style.backgroundColor = 'var(--theme-info-background)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--theme-card-border)';
                          e.currentTarget.style.backgroundColor = 'var(--theme-card-background)';
                        }}
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                          }}
                        >
                          <span className="text-white font-bold text-sm">
                            {account.username?.[0]?.toUpperCase() || 'I'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-sm font-medium truncate"
                            style={{ color: 'var(--theme-card-text)' }}
                          >
                            @{account.username}
                          </p>
                          <p
                            className="text-xs truncate"
                            style={{ color: 'var(--theme-card-muted)' }}
                          >
                            {account.name} · Página: {account.pageName}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>

                  <Button
                    onClick={() => {
                      resetEmbedded();
                      setErrorMessage(null);
                    }}
                    variant="secondary"
                    className="w-full"
                  >
                    Cancelar e tentar novamente
                  </Button>
                </div>
              ) : (
                <>
                  {/* Facebook Login Button */}
                  <Button
                    onClick={startEmbeddedSignup}
                    disabled={isEmbeddedLoading}
                    className="w-full flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: '#1877F2',
                      color: 'white',
                    }}
                  >
                    <Icon name="facebook" size={20} />
                    {isEmbeddedLoading ? 'Conectando...' : 'Conectar com Instagram'}
                  </Button>

                  <p
                    className="text-xs text-center"
                    style={{ color: 'var(--theme-card-muted)' }}
                  >
                    Método recomendado - configuração automática
                  </p>
                </>
              )}
            </div>
          )}

          {/* ============================================= */}
          {/* MANUAL MODE - Credenciais Manuais            */}
          {/* ============================================= */}
          {authMode === 'manual' && (
            <div className="space-y-4">
              {/* Channel Name (optional, auto-fills) */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Nome do Canal
                </label>
                <Input
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="Ex: Instagram Suporte"
                  className="w-full"
                  data-testid="instagram-form-channel-name"
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Se não informado, será gerado a partir do username do Instagram
                </p>
              </div>

              {/* Access Token + botão Conectar grudado à direita */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Access Token *
                </label>
                <div className="flex items-stretch gap-0">
                  <div className="relative flex-1">
                    <Icon name="key" size={18}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                      style={{ color: 'var(--theme-card-muted)' }} />
                    <Input
                      type="password"
                      value={accessToken}
                      onChange={(e) => {
                        setAccessToken(e.target.value);
                        if (pages.length > 0) resetManualDiscovery();
                      }}
                      placeholder="EAAxxxxxxxxxxxxxxx"
                      className="w-full pl-10"
                      style={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        borderRight: 'none',
                      }}
                      data-testid="instagram-form-access-token"
                    />
                  </div>
                  <Button
                    onClick={handleConnectToken}
                    disabled={isConnectingToken || !accessToken.trim()}
                    className="flex-shrink-0"
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      height: '36px',
                    }}
                    data-testid="instagram-form-connect-token-button"
                  >
                    {isConnectingToken ? (
                      <>
                        <Icon name="loader" size={14} className="mr-1.5 animate-spin" />
                        Conectando...
                      </>
                    ) : pages.length > 0 ? (
                      <>
                        <Icon name="refresh" size={14} className="mr-1.5" />
                        Reconectar
                      </>
                    ) : (
                      <>Conectar</>
                    )}
                  </Button>
                </div>
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Cole o token gerado no <a
                    href="https://developers.facebook.com/tools/explorer/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ color: 'var(--theme-info-color)' }}
                  >Graph API Explorer</a> e clique em Conectar
                </p>

                {tokenError && (
                  <div
                    className="mt-2 p-2.5 rounded-lg flex items-start gap-2"
                    style={{
                      backgroundColor: 'var(--theme-error-background)',
                      border: '1px solid var(--theme-error-color)',
                    }}
                    data-testid="instagram-form-token-error"
                  >
                    <Icon name="alert-circle" size={16}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: 'var(--theme-error-color)' }} />
                    <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
                      {tokenError}
                    </p>
                  </div>
                )}
              </div>

              {/* Alerta de expiração / scopes faltantes */}
              {tokenInfo && (
                <TokenStatusAlert
                  info={tokenInfo}
                  testIdPrefix="instagram-form"
                  accessToken={accessToken}
                />
              )}

              {/* Page select — visível após Conectar com sucesso */}
              {pages.length > 0 && (
                <Select
                  label="Página do Facebook"
                  required
                  options={pages.map((p) => ({
                    value: p.id,
                    label: `${p.instagramBusinessAccountId ? '✅' : '⚠️'} ${p.name} (${p.id})`,
                  }))}
                  value={selectedPageId}
                  onChange={handleSelectPage}
                  placeholder="Selecione a página vinculada ao Instagram"
                  description="Páginas com ✅ já têm Instagram Business vinculado"
                  data-testid="instagram-form-page-select"
                />
              )}

              {/* Detecção do IG Business Account */}
              {selectedPageId && isDetectingIg && (
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--theme-card-muted)' }}>
                  <Icon name="loader" size={14} className="animate-spin" />
                  Buscando Instagram Business Account...
                </div>
              )}

              {igDetectError && (
                <div
                  className="p-3 rounded-lg flex items-start gap-2"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                  data-testid="instagram-form-detect-error"
                >
                  <Icon name="alert-circle" size={18}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: 'var(--theme-error-color)' }} />
                  <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
                    {igDetectError}
                  </p>
                </div>
              )}

              {/* Sucesso — IG detectado */}
              {instagramAccountId && !isDetectingIg && (
                <div
                  className="p-3 rounded-lg flex items-start gap-2"
                  style={{
                    backgroundColor: 'var(--theme-success-background)',
                    border: '1px solid var(--theme-success-color)',
                  }}
                  data-testid="instagram-form-detect-success"
                >
                  <Icon name="check-circle" size={18}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: 'var(--theme-success-color)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-success-color)' }}>
                      Instagram Business Account detectado
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--theme-card-muted)' }}>
                      {igUsername ? `@${igUsername} · ` : ''}ID: {instagramAccountId}
                    </p>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                  data-testid="instagram-form-error"
                >
                  <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
                    {errorMessage}
                  </p>
                </div>
              )}

              <Button
                onClick={handleConnect}
                className="w-full"
                disabled={!instagramAccountId || !pageId}
                data-testid="instagram-form-submit"
              >
                {!instagramAccountId || !pageId
                  ? 'Selecione uma página com Instagram para continuar'
                  : 'Conectar Instagram'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Connecting */}
      {step === 'connecting' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center animate-pulse"
              style={{ backgroundColor: 'var(--theme-button-primary)' }}
            >
              <Icon name="key" size={32} className="text-white" />
            </div>
          </div>
          <p
            className="text-lg font-medium"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Conectando ao Instagram...
          </p>
          <p
            className="text-sm mt-2"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Validando credenciais e configurando webhook
          </p>
        </div>
      )}

      {/* Step 3: Show Webhook URL */}
      {step === 'webhook' && (
        <div className="space-y-4">
          <div
            className="p-4 rounded-lg flex items-start gap-3"
            style={{
              backgroundColor: 'var(--theme-success-background)',
              border: '1px solid var(--theme-success-color)',
            }}
          >
            <Icon name="check-circle" size={20}
              style={{ color: 'var(--theme-success-color)' }}
              className="flex-shrink-0 mt-0.5" />
            <div>
              <p
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Credenciais validadas com sucesso!
              </p>
              <p
                className="text-sm mt-1"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                Agora configure o webhook no Meta Business
              </p>
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              URL do Webhook
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={webhookUrl}
                readOnly
                className="flex-1"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  color: 'var(--theme-card-muted)',
                }}
              />
              <Button
                onClick={handleCopyWebhook}
                variant="secondary"
                className="flex-shrink-0"
              >
                <Icon name="copy" size={16} />
              </Button>
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Copie esta URL e configure no Meta for Developers
            </p>
          </div>

          <div
            className="p-4 rounded-lg space-y-2"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
            }}
          >
            <p
              className="text-sm font-medium"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Como configurar o webhook:
            </p>
            <ol
              className="text-sm space-y-1 list-decimal list-inside"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              <li>Acesse o Meta for Developers</li>
              <li>Vá no seu App &gt; Instagram &gt; Webhooks</li>
              <li>Cole a URL do webhook acima</li>
              <li>Configure o token de verificação (se solicitado)</li>
              <li>Inscreva-se nos eventos de mensagem</li>
            </ol>
            <a
              href="https://developers.facebook.com/docs/graph-api/webhooks/getting-started"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm flex items-center gap-1 hover:underline mt-2"
              style={{ color: 'var(--theme-info-color)' }}
            >
              Ver documentação completa
              <Icon name="external-link" size={14} />
            </a>
          </div>

          <Button
            onClick={handleComplete}
            className="w-full"
            data-testid="instagram-form-complete-button"
          >
            Concluir Configuração
          </Button>
        </div>
      )}
    </div>
  );
};

InstagramForm.displayName = 'InstagramForm';
